import React from "react";
import { render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";

import { FullPageChatLayout } from "./FullPageChatLayout";
import {
  INTERRUPTED_NOTICE_TEXT,
  mapConversationMessages,
  type IDAChatMessage,
} from "../utils/chatMessageMapper";

// ChatMessage from the shared package pulls in heavy context; the notice is
// rendered by the layout around it, so a stub is enough.
jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: ({
    message,
  }: {
    message: { content?: string; isStreaming?: boolean };
  }) => (
    <div data-testid={`chat-message-${message.content}`}>
      {message.content}
      {message.isStreaming && <span data-testid="streaming-cursor">▊</span>}
    </div>
  ),
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: any) => any) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
});

function makeAssistant(overrides: Partial<IDAChatMessage>): IDAChatMessage {
  return {
    id: "a1",
    role: "assistant",
    content: "Half an answer",
    createdAt: new Date("2026-09-23T10:00:00Z"),
    ...overrides,
  } as IDAChatMessage;
}

const renderWith = (messages: IDAChatMessage[]) =>
  render(
    <FullPageChatLayout
      messages={messages}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={[]}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={7}
      welcomeTitle="Welcome"
      welcomeDescription=""
    />,
  );

describe("FullPageChatLayout interrupted-notice surface (#4981)", () => {
  it("renders the notice under an assistant row flagged interrupted", () => {
    renderWith([makeAssistant({ interrupted: true })]);
    const notice = screen.getByTestId("interrupted-notice");
    expect(notice).toBeInTheDocument();
    expect(notice).toHaveTextContent(
      "Connection interrupted — response may be incomplete",
    );
    expect(INTERRUPTED_NOTICE_TEXT).toContain("Send your prompt again");
  });

  it("renders no notice for a complete assistant row", () => {
    renderWith([makeAssistant({})]);
    expect(screen.queryByTestId("interrupted-notice")).not.toBeInTheDocument();
  });

  it("shows a user-stop label for an interrupted row stopped by the user", () => {
    renderWith([
      makeAssistant({ interrupted: true, stoppedByUser: true } as any),
    ]);
    expect(screen.getByTestId("interrupted-notice")).toHaveTextContent(
      "Stopped by you.",
    );
  });

  it("restores an interrupted persisted row without a streaming cursor", () => {
    const [restored] = mapConversationMessages([
      {
        id: 506,
        role: "assistant",
        content: "Half an answer",
        created_at: "2026-09-23T10:00:00Z",
        metadata: { interrupted: true },
      },
    ]);

    expect(restored.isStreaming).toBe(false);
    renderWith([restored]);
    expect(screen.queryByTestId("streaming-cursor")).not.toBeInTheDocument();
  });

  // NB: not "user role never gets the notice" — getMessagePresentationRole
  // renders Hub-delegated user rows as assistant bubbles, so a flagged row
  // addressed to the agent correctly shows the notice too. The case kept is
  // that non-assistant-presentation system rows never do.
  it("renders no notice on a system row even if flagged", () => {
    renderWith([
      makeAssistant({ id: "s1", role: "system", interrupted: true }),
    ]);
    expect(screen.queryByTestId("interrupted-notice")).not.toBeInTheDocument();
  });
});
